// Tool-specific approval `form_data` builder (fn-69.2, R4).
//
// The driver does NOT blindly echo `tool_args` as `form_data`. Each gate
// needs a different ack payload — the CP validates the shape per
// `ack_kind` + tool, and a wrong payload 400s (or worse, writes a wrong
// matter). This builder is keyed on the BARE tool name:
//
//   - `review_matter` (immediate) → the confirmed matter draft. Defaults
//     to `tool_args` (the assembled draft IS the render payload, memory
//     `project_matter_creator_e2e_findings`); a caller-edited matter
//     overrides it.
//   - `ask_user_question` (immediate) → valid DEFAULT answers built to
//     pass `validateAskUserAnswers`: echo the questions array WITH
//     options + a keyed `answers` map (first option per question; a list
//     for multi-select). Matches the elicitation form shape.
//   - `create_contact` / `create_matter` (tool_confirmation) → NO
//     `form_data`; the decision is just `{decision}`.
//   - anything else (unknown immediate custom) → no synthesizable
//     payload; return `undefined` so the driver fails closed (ask a
//     human) rather than blind-approving.

import { ASK_USER_QUESTION_TOOL, bareToolName } from './approval-policy.js';

export interface FormDataGate {
  readonly tool_name: string;
  readonly tool_args: Record<string, unknown>;
}

export interface FormDataOptions {
  /**
   * A caller-edited matter draft for `review_matter`. When supplied it
   * replaces `tool_args` as the confirmed matter. Ignored for other
   * tools.
   */
  readonly matterOverride?: Record<string, unknown>;
}

/**
 * Build the `form_data` for a gate's approval, or `undefined` when the
 * tool takes none (`create_*` / unknown). Pure.
 */
export function formDataForGate(
  gate: FormDataGate,
  opts: FormDataOptions = {},
): Record<string, unknown> | undefined {
  const bare = bareToolName(gate.tool_name);

  if (bare === 'review_matter') {
    // The confirmed (possibly edited) matter draft.
    return opts.matterOverride ?? gate.tool_args;
  }

  if (bare === ASK_USER_QUESTION_TOOL) {
    return buildDefaultAnswers(gate.tool_args);
  }

  // create_contact / create_matter (tool_confirmation): no form_data.
  // Unknown immediate custom tools: no synthesizable default → fail
  // closed (the driver routes these to `ask`).
  return undefined;
}

/**
 * An EXPLICIT auto-approval plan that disambiguates the three meanings the
 * bare `formDataForGate` `undefined` conflated. Lights-out, an auto-decision
 * must NEVER post `decision:'approved'` with a missing payload for a tool
 * that REQUIRES one — that turns a malformed `ask_user_question` into an
 * invalid approval instead of a human fallback. The caller MUST branch on
 * `kind` before approving:
 *
 *   - `post` — safe to auto-approve: `formData` is present, OR the tool
 *     legitimately takes none (`create_*` / `tool_confirmation`).
 *   - `needs-human` — an immediate-custom tool (`ask_user_question`) that
 *     REQUIRES a synthesized payload we could NOT build (malformed args).
 *     Fail closed: surface it for human review, never auto-approve.
 */
export type AutoApprovalPlan =
  | { readonly kind: 'post'; readonly formData?: Record<string, unknown> }
  | { readonly kind: 'needs-human' };

export function planFormDataForApproval(
  gate: FormDataGate,
  opts: FormDataOptions = {},
): AutoApprovalPlan {
  const formData = formDataForGate(gate, opts);
  if (formData !== undefined) {
    return { kind: 'post', formData };
  }
  // `undefined` → either the tool needs no form_data (create_*), or an
  // immediate-custom tool needed a payload we couldn't synthesize.
  if (bareToolName(gate.tool_name) === ASK_USER_QUESTION_TOOL) {
    return { kind: 'needs-human' }; // malformed elicitation → human review
  }
  return { kind: 'post' }; // create_* / tool_confirmation: no payload needed
}

interface ElicitationOption {
  readonly label: string;
  readonly description?: string;
}
interface ElicitationQuestion {
  readonly question: string;
  readonly header?: string;
  readonly options: readonly ElicitationOption[];
  readonly multiSelect?: boolean;
}

/**
 * Build a default-answer payload for an `ask_user_question` gate that
 * passes `validateAskUserAnswers`: every question is answered with its
 * FIRST option label (a single string for single-select, a one-element
 * list for multi-select), and the full `questions` array is echoed.
 *
 * Returns `undefined` when `tool_args` is not a well-formed elicitation
 * offer (no answerable questions) — the driver then fails closed rather
 * than posting an invalid ack.
 */
export function buildDefaultAnswers(
  toolArgs: Record<string, unknown>,
):
  | { questions: readonly ElicitationQuestion[]; answers: Record<string, string | string[]> }
  | undefined {
  const rawQuestions = (toolArgs as { questions?: unknown }).questions;
  // Mirror the FULL `validateAskUserAnswers` shape (both bounds): 1–4
  // questions, each with a non-empty unique `question`, an optional
  // header ≤12 chars, an optional boolean `multiSelect`, and 2–4 options
  // with non-empty unique labels. Any violation → `undefined` so the
  // driver fails closed to human review rather than posting `form_data`
  // the server would 400 (which, lights-out, would turn an auto-answer
  // into a failed approval instead of a fallback to a human).
  if (!Array.isArray(rawQuestions) || rawQuestions.length === 0 || rawQuestions.length > 4) {
    return undefined;
  }

  const questions: ElicitationQuestion[] = [];
  const answers: Record<string, string | string[]> = {};
  const seenQuestions = new Set<string>();

  for (const rawQ of rawQuestions) {
    if (rawQ === null || typeof rawQ !== 'object' || Array.isArray(rawQ)) {
      return undefined;
    }
    const q = rawQ as {
      question?: unknown;
      header?: unknown;
      options?: unknown;
      multiSelect?: unknown;
    };
    if (typeof q.question !== 'string' || q.question.length === 0) {
      return undefined;
    }
    if (seenQuestions.has(q.question)) {
      return undefined;
    }
    seenQuestions.add(q.question);
    if (q.header !== undefined && (typeof q.header !== 'string' || q.header.length > 12)) {
      return undefined;
    }
    if (q.multiSelect !== undefined && typeof q.multiSelect !== 'boolean') {
      return undefined;
    }
    if (!Array.isArray(q.options) || q.options.length < 2 || q.options.length > 4) {
      return undefined;
    }
    const options: ElicitationOption[] = [];
    const seenLabels = new Set<string>();
    for (const rawO of q.options) {
      if (rawO === null || typeof rawO !== 'object' || Array.isArray(rawO)) {
        return undefined;
      }
      const o = rawO as { label?: unknown; description?: unknown };
      if (typeof o.label !== 'string' || o.label.length === 0) {
        return undefined;
      }
      if (seenLabels.has(o.label)) {
        return undefined;
      }
      seenLabels.add(o.label);
      if (o.description !== undefined && typeof o.description !== 'string') {
        return undefined;
      }
      const opt: { label: string; description?: string } = { label: o.label };
      if (typeof o.description === 'string') {
        opt.description = o.description;
      }
      options.push(opt);
    }

    const multiSelect = q.multiSelect === true;
    const question: {
      question: string;
      header?: string;
      options: ElicitationOption[];
      multiSelect?: boolean;
    } = {
      question: q.question,
      options,
    };
    if (typeof q.header === 'string') {
      question.header = q.header;
    }
    if (multiSelect) {
      question.multiSelect = true;
    }
    questions.push(question);

    const firstOption = options[0];
    if (firstOption === undefined) {
      return undefined;
    }
    answers[q.question] = multiSelect ? [firstOption.label] : firstOption.label;
  }

  return { questions, answers };
}
